import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Track.css';
import { API_BASE_URL } from '../config';
import ManualSleepEntry from '../components/ManualSleepEntry';
import EditSleepEntry from '../components/EditSleepEntry';
import SleepWheel from '../components/SleepWheel';
import { useWebSocket } from '../contexts/WebSocketContext';
import { formatDateTimeRelative } from '../utils/timeFormat';
import { useToast } from '../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';

interface Baby {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  is_owner?: boolean;
  owner_username?: string;
}

interface SleepEntry {
  id: string;
  baby_id: string;
  start_time: string;
  end_time: string | null;
  created_at: string;
  is_ongoing: boolean;
  duration_minutes: number | null;
}

interface PredictedSlot {
  id: string;
  predicted_start_time: string;
  predicted_end_time: string;
  slot_type: 'nap' | 'night_sleep';
  confidence_score?: number;
}

interface PredictionResponse {
  id: string;
  baby_id: string;
  predicted_slots: PredictedSlot[];
  confidence: number;
  reasoning: string;
  created_at: string;
  status: string;
}

interface TrackProps {
  user: any;
}

const Track: React.FC<TrackProps> = ({ user }) => {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [sleepEntries, setSleepEntries] = useState<SleepEntry[]>([]);
  const [ongoingSleep, setOngoingSleep] = useState<SleepEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [selectedSleepEntry, setSelectedSleepEntry] = useState<SleepEntry | null>(null);
  const [isEditingEntry, setIsEditingEntry] = useState(false);
  const [isEditingAfterStop, setIsEditingAfterStop] = useState(false);
  const [startTimeChanged, setStartTimeChanged] = useState(false);
  const [nextPrediction, setNextPrediction] = useState<{
    slot: PredictedSlot;
    displayMode: 'waking' | 'should-be-sleeping' | 'future';
  } | null>(null);
  const [loadingPrediction, setLoadingPrediction] = useState(false);
  const [, setTimerTick] = useState(0); // For triggering timer updates
  const sleepListRef = useRef<HTMLDivElement>(null);

  const { lastMessage } = useWebSocket();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Find active baby based on user's active_baby_id
  const activeBaby = babies.find(baby => baby.id === user.active_baby_id) || babies[0];

  // Group sleep entries by date
  const groupEntriesByDate = (entries: SleepEntry[]) => {
    const groups: { [key: string]: SleepEntry[] } = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    entries.forEach(entry => {
      const entryDate = new Date(entry.start_time);
      const dateKey = entryDate.toDateString();
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });

    return groups;
  };

  // Get date header text
  const getDateHeader = (dateString: string) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const entryDate = new Date(dateString);
    
    if (entryDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (entryDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return entryDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const fetchBabies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBabies(data);
      }
    } catch (error) {
      console.error('Failed to fetch babies:', error);
    }
  }, [user.id]);

  const fetchSleepEntries = useCallback(async () => {
    if (!activeBaby) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/baby/${activeBaby.id}`);
      if (response.ok) {
        const data = await response.json();
        setSleepEntries(data);
      }
    } catch (error) {
      console.error('Failed to fetch sleep entries:', error);
    } finally {
      setLoading(false);
    }
  }, [activeBaby]);

  const fetchOngoingSleep = useCallback(async () => {
    if (!activeBaby) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/baby/${activeBaby.id}/ongoing`);
      if (response.ok) {
        const data = await response.json();
        setOngoingSleep(data);
      }
    } catch (error) {
      console.error('Failed to fetch ongoing sleep:', error);
    }
  }, [activeBaby]);

  const fetchNextPrediction = useCallback(async (shouldPoll: boolean = false) => {
    if (!activeBaby) return;

    setLoadingPrediction(true);

    const MAX_RETRIES = 7; // Poll for up to 14 seconds (7 retries * 2 seconds)

    const fetchWithPolling = async (retryCount: number = 0): Promise<void> => {
      try {
        // Get user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await fetch(`${API_BASE_URL}/api/sleep/predict/${activeBaby.id}/latest?timezone=${encodeURIComponent(timezone)}`);

        if (response.ok) {
          const prediction: PredictionResponse = await response.json();
          console.log(`Track: Received prediction (attempt ${retryCount + 1}):`, prediction);
          console.log('Track: Prediction slots:', prediction?.predicted_slots);

          // If we got a prediction with slots, process it
          if (prediction && prediction.predicted_slots && prediction.predicted_slots.length > 0) {
            const now = new Date();
            console.log('Track: Current time:', now);
            console.log('Track: Number of slots:', prediction.predicted_slots.length);

            // Case 1: If baby is sleeping (ongoingSleep exists)
            if (ongoingSleep) {
              console.log('Track: Baby is sleeping, looking for wake-up slot');
              const wakeUpSlot = prediction.predicted_slots.find(slot =>
                new Date(slot.predicted_start_time) <= now &&
                new Date(slot.predicted_end_time) > now
              );

              if (wakeUpSlot) {
                console.log('Track: Found wake-up slot:', wakeUpSlot);
                setNextPrediction({
                  slot: wakeUpSlot,
                  displayMode: 'waking'
                });
                setLoadingPrediction(false);
                return;
              } else {
                console.log('Track: No wake-up slot found');
              }
            }

            // Case 2: Baby should be sleeping but isn't
            if (!ongoingSleep) {
              console.log('Track: Baby is awake, checking if should be sleeping');
              const missedSlot = prediction.predicted_slots.find(slot =>
                new Date(slot.predicted_start_time) <= now &&
                new Date(slot.predicted_end_time) > now
              );

              if (missedSlot) {
                console.log('Track: Baby should be sleeping now:', missedSlot);
                setNextPrediction({
                  slot: missedSlot,
                  displayMode: 'should-be-sleeping'
                });
                setLoadingPrediction(false);
                return;
              } else {
                console.log('Track: No current sleep slot');
              }
            }

            // Case 3: Normal future prediction
            const futureSlots = prediction.predicted_slots
              .filter(slot => new Date(slot.predicted_start_time) > now)
              .sort((a, b) => new Date(a.predicted_start_time).getTime() - new Date(b.predicted_start_time).getTime());

            console.log('Track: Future slots:', futureSlots.length);
            if (futureSlots.length > 0) {
              console.log('Track: Setting next prediction (future):', futureSlots[0]);
              setNextPrediction({
                slot: futureSlots[0],
                displayMode: 'future'
              });
              setLoadingPrediction(false);
              return;
            } else {
              console.log('Track: No future slots found');
              setNextPrediction(null);
              setLoadingPrediction(false);
              return;
            }
          } else {
            // No slots in prediction
            console.log('Track: No prediction or no predicted_slots');

            // If polling is enabled and we haven't exceeded max retries, try again
            if (shouldPoll && retryCount < MAX_RETRIES) {
              console.log(`Track: Polling enabled, will retry in 2 seconds (attempt ${retryCount + 1}/${MAX_RETRIES})`);
              setTimeout(() => {
                fetchWithPolling(retryCount + 1);
              }, 2000);
              return;
            } else {
              // No more retries or polling disabled
              console.log('Track: No more retries, setting prediction to null');
              setNextPrediction(null);
              setLoadingPrediction(false);
              return;
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch prediction:', error);
        setLoadingPrediction(false);
      }
    };

    // Start fetching (with or without polling)
    await fetchWithPolling(0);
  }, [activeBaby, ongoingSleep]);

  useEffect(() => {
    if (user) {
      fetchBabies();
    }
  }, [user, fetchBabies]);

  useEffect(() => {
    if (activeBaby) {
      fetchSleepEntries();
      fetchOngoingSleep();
      fetchNextPrediction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBaby, fetchSleepEntries, fetchOngoingSleep]); // Removed fetchNextPrediction to prevent infinite loop

  // Refresh prediction when ongoing sleep state changes
  useEffect(() => {
    if (activeBaby) {
      // Enable polling when ongoing sleep changes (start/stop triggers prediction generation)
      fetchNextPrediction(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ongoingSleep?.id]); // Only trigger when ongoing sleep ID changes, not the whole object

  // Update timer every second when sleep is ongoing
  useEffect(() => {
    if (!ongoingSleep) return;

    // Update every second to re-render and show updated duration
    const interval = setInterval(() => {
      setTimerTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [ongoingSleep]); // Restart interval when sleep changes

  // Handle WebSocket messages
  useEffect(() => {
    console.log('🔄 WebSocket effect triggered');
    console.log('lastMessage:', lastMessage);
    console.log('activeBaby:', activeBaby);

    if (!lastMessage || !activeBaby) {
      console.log('❌ Skipping: no message or no active baby');
      return;
    }

    const { type, data } = lastMessage;

    console.log('📨 Processing message - Type:', type, 'Baby ID:', data?.baby_id, 'Active Baby ID:', activeBaby.id);

    // Only process messages for the active baby
    if (data?.baby_id !== activeBaby.id) {
      console.log('❌ Skipping: message not for active baby');
      return;
    }

    console.log('✅ Processing WebSocket message:', type, data);

    if (type === 'sleep_started') {
      console.log('💤 Starting sleep tracking for baby');
      // Update ongoing sleep
      setOngoingSleep(data);
      // Add to sleep entries at the beginning
      setSleepEntries(prev => [data, ...prev]);
    } else if (type === 'sleep_stopped') {
      console.log('😴 Stopping sleep tracking for baby');
      // Clear ongoing sleep
      setOngoingSleep(null);
      // Update the sleep entry in the list
      setSleepEntries(prev =>
        prev.map(entry => entry.id === data.id ? data : entry)
      );
    } else if (type === 'sleep_updated') {
      console.log('⏱️ Sleep entry updated for baby');
      // Update ongoing sleep if it's the same entry
      if (ongoingSleep?.id === data.id) {
        setOngoingSleep(data);
      }
      // Update in sleep entries list
      setSleepEntries(prev =>
        prev.map(entry => entry.id === data.id ? data : entry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, activeBaby]);


  // Auto-scroll to bottom when sleep entries change
  useEffect(() => {
    if (sleepListRef.current && sleepEntries.length > 0) {
      sleepListRef.current.scrollTop = sleepListRef.current.scrollHeight;
    }
  }, [sleepEntries]);

  const startSleep = async () => {
    if (!activeBaby) return;
    
    console.log('🚀 Starting sleep for baby:', activeBaby.id);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baby_id: activeBaby.id,
        }),
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const newSleep = await response.json();
        console.log('Sleep started successfully:', newSleep);
        setOngoingSleep(newSleep);
        fetchSleepEntries(); // Refresh the list
      } else {
        console.error('Failed to start sleep - HTTP error:', response.status);
      }
    } catch (error) {
      console.error('Failed to start sleep:', error);
    }
  };

  const stopSleep = async () => {
    if (!ongoingSleep) return;

    console.log('🛑 Stopping sleep for entry:', ongoingSleep.id);

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/stop/${ongoingSleep.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      console.log('Stop response status:', response.status);

      if (response.ok) {
        const stoppedSleep = await response.json();
        console.log('Sleep stopped successfully:', stoppedSleep);

        // Auto-save and enter edit mode
        setOngoingSleep(null);
        setSelectedSleepEntry(stoppedSleep);
        setIsEditingEntry(true);
        setIsEditingAfterStop(true);
        fetchSleepEntries(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to stop sleep:', error);
      showToast('Failed to stop sleep', 'error');
    }
  };

  const adjustStartTime = async (minutes: number) => {
    if (!ongoingSleep) return;

    // Calculate new start time
    const currentStart = new Date(ongoingSleep.start_time);
    const newStart = new Date(currentStart.getTime() + minutes * 60 * 1000);

    // Client-side validation
    const now = new Date();
    if (newStart > now) {
      console.warn('Cannot set start time in the future');
      return;
    }

    // Check for overlap with other sleep entries
    // For ongoing sleep, end time is "now"
    const wouldOverlap = sleepEntries.some(entry => {
      // Skip the current ongoing entry
      if (entry.id === ongoingSleep.id) return false;

      // Skip other ongoing entries (shouldn't exist, but just in case)
      if (!entry.end_time) return false;

      const entryStart = new Date(entry.start_time);
      const entryEnd = new Date(entry.end_time);

      // Check if [newStart, now] overlaps with [entryStart, entryEnd]
      return newStart < entryEnd && now > entryStart;
    });

    if (wouldOverlap) {
      console.warn('Cannot adjust: would overlap with another sleep entry');
      return;
    }

    console.log(`⏱️ Adjusting start time by ${minutes} minute(s)`);

    // Optimistic UI update
    const previousStartTime = ongoingSleep.start_time;
    setOngoingSleep({
      ...ongoingSleep,
      start_time: newStart.toISOString().replace('Z', '')
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/${ongoingSleep.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_time: newStart.toISOString()
        }),
      });

      if (!response.ok) {
        // Revert optimistic update on error
        setOngoingSleep({
          ...ongoingSleep,
          start_time: previousStartTime
        });
        console.error('Failed to adjust start time - HTTP error:', response.status);
        return;
      }

      const updatedSleep = await response.json();
      console.log('Start time adjusted successfully:', updatedSleep);

      // Trigger flash animation
      setStartTimeChanged(true);
      setTimeout(() => {
        setStartTimeChanged(false);
      }, 400); // Match animation duration

      // Update in sleep entries list
      setSleepEntries(prev =>
        prev.map(entry =>
          entry.id === ongoingSleep.id
            ? { ...entry, start_time: updatedSleep.start_time }
            : entry
        )
      );
    } catch (error) {
      // Revert optimistic update on error
      setOngoingSleep({
        ...ongoingSleep,
        start_time: previousStartTime
      });
      console.error('Failed to adjust start time:', error);
    }
  };


  const handleManualSleepSubmit = async (startTime: Date, endTime: Date) => {
    if (!activeBaby) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baby_id: activeBaby.id,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
        }),
      });

      if (response.ok) {
        fetchSleepEntries(); // Refresh the list
      } else {
        throw new Error('Failed to create sleep entry');
      }
    } catch (error) {
      console.error('Failed to create manual sleep entry:', error);
      throw error; // Re-throw to show error in modal
    }
  };

  const handleSleepEntryClick = (entry: SleepEntry) => {
    // Don't allow editing the ongoing sleep entry itself (but allow editing other entries)
    if (entry.is_ongoing) return;

    setSelectedSleepEntry(entry);
    setIsEditingEntry(true);
  };

  const handleUpdateSleepEntry = async (sleepId: string, startTime: Date, endTime: Date) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/${sleepId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
        }),
      });

      if (response.ok) {
        fetchSleepEntries(); // Refresh the list
        fetchOngoingSleep(); // Check if ongoing sleep changed
      } else {
        throw new Error('Failed to update sleep entry');
      }
    } catch (error) {
      console.error('Failed to update sleep entry:', error);
      throw error;
    }
  };

  const handleDeleteSleepEntry = async (sleepId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/${sleepId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchSleepEntries(); // Refresh the list
        fetchOngoingSleep(); // Check if ongoing sleep changed
      } else {
        throw new Error('Failed to delete sleep entry');
      }
    } catch (error) {
      console.error('Failed to delete sleep entry:', error);
      throw error;
    }
  };

  const checkOverlapWithOtherEntries = (startDateTime: Date, endDateTime: Date, currentEntryId: string): boolean => {
    // Check if the proposed time range overlaps with any other sleep entry
    return sleepEntries.some(entry => {
      // Skip the entry we're currently editing
      if (entry.id === currentEntryId) return false;

      // Skip ongoing entries (they have no end time)
      if (!entry.end_time) return false;

      // Parse entry times as UTC
      const entryStart = new Date(entry.start_time);
      const entryEnd = new Date(entry.end_time);

      // Check for overlap: (newStart < otherEnd) AND (newEnd > otherStart)
      return startDateTime < entryEnd && endDateTime > entryStart;
    });
  };

  const handleDoneEdit = () => {
    setIsEditingEntry(false);
    setSelectedSleepEntry(null);
    setIsEditingAfterStop(false);
  };

  const handleDeleteFromEdit = async () => {
    if (!selectedSleepEntry) return;

    if (window.confirm('Delete this sleep entry? This action cannot be undone.')) {
      try {
        await handleDeleteSleepEntry(selectedSleepEntry.id);
        setIsEditingEntry(false);
        setSelectedSleepEntry(null);
      } catch (err) {
        showToast('Failed to delete sleep entry', 'error');
      }
    }
  };

  const handleAdjustEditStart = async (minutes: number) => {
    if (!selectedSleepEntry || !selectedSleepEntry.end_time) return;

    // Parse current values from selected entry
    const currentStartDateTime = new Date(selectedSleepEntry.start_time);
    const newStartDateTime = new Date(currentStartDateTime.getTime() + minutes * 60 * 1000);
    const endDateTime = new Date(selectedSleepEntry.end_time);

    // Client-side validation
    const now = new Date();
    if (newStartDateTime > now) {
      showToast('Start time cannot be in the future', 'error');
      return;
    }

    if (newStartDateTime >= endDateTime) {
      showToast('Start time must be before end time', 'error');
      return;
    }

    const durationHours = (endDateTime.getTime() - newStartDateTime.getTime()) / (1000 * 60 * 60);
    if (durationHours > 24) {
      showToast('Sleep duration cannot be more than 24 hours', 'error');
      return;
    }

    // Check for overlap with other sleep entries
    if (checkOverlapWithOtherEntries(newStartDateTime, endDateTime, selectedSleepEntry.id)) {
      showToast('Sleep times cannot overlap with another sleep entry', 'error');
      return;
    }

    // Auto-save
    try {
      await handleUpdateSleepEntry(selectedSleepEntry.id, newStartDateTime, endDateTime);
      // Update selected entry with the new times immediately (don't wait for fetch)
      const durationMs = endDateTime.getTime() - newStartDateTime.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      setSelectedSleepEntry({
        ...selectedSleepEntry,
        start_time: newStartDateTime.toISOString(),
        duration_minutes: durationMinutes
      });
    } catch (err) {
      showToast('Failed to update sleep entry', 'error');
    }
  };

  const handleAdjustEditEnd = async (minutes: number) => {
    if (!selectedSleepEntry || !selectedSleepEntry.end_time) return;

    // Parse current values from selected entry
    const startDateTime = new Date(selectedSleepEntry.start_time);
    const currentEndDateTime = new Date(selectedSleepEntry.end_time);
    const newEndDateTime = new Date(currentEndDateTime.getTime() + minutes * 60 * 1000);

    // Client-side validation
    const now = new Date();
    if (newEndDateTime > now) {
      showToast('End time cannot be in the future', 'error');
      return;
    }

    if (newEndDateTime <= startDateTime) {
      showToast('End time must be after start time', 'error');
      return;
    }

    const durationHours = (newEndDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    if (durationHours > 24) {
      showToast('Sleep duration cannot be more than 24 hours', 'error');
      return;
    }

    // Check for overlap with other sleep entries
    if (checkOverlapWithOtherEntries(startDateTime, newEndDateTime, selectedSleepEntry.id)) {
      showToast('Sleep times cannot overlap with another sleep entry', 'error');
      return;
    }

    // Auto-save
    try {
      await handleUpdateSleepEntry(selectedSleepEntry.id, startDateTime, newEndDateTime);
      // Update selected entry with the new times immediately (don't wait for fetch)
      const durationMs = newEndDateTime.getTime() - startDateTime.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      setSelectedSleepEntry({
        ...selectedSleepEntry,
        end_time: newEndDateTime.toISOString(),
        duration_minutes: durationMinutes
      });
    } catch (err) {
      showToast('Failed to update sleep entry', 'error');
    }
  };

  const formatTimeOnly = (dateString: string) => {
    // Parse as UTC time since server stores UTC without timezone info
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: user.time_format_24h ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: !user.time_format_24h
    });
  };

  const formatDateTimeForUser = (dateString: string) => {
    // Parse as UTC time since server stores UTC without timezone info
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const timeString = date.toLocaleTimeString([], {
      hour: user.time_format_24h ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: !user.time_format_24h
    });

    if (dateOnly.getTime() === today.getTime()) {
      return `today at ${timeString}`;
    } else {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (dateOnly.getTime() === yesterday.getTime()) {
        return `yesterday at ${timeString}`;
      } else {
        return date.toLocaleDateString() + ` at ${timeString}`;
      }
    }
  };

  const formatDuration = (minutes: number | null) => {
    if (minutes === null) return 'Ongoing';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getOngoingDuration = () => {
    if (!ongoingSleep) return '';

    // Parse the start time as UTC since server stores UTC without timezone info
    const startTimeString = ongoingSleep.start_time;
    const startTime = new Date(startTimeString); // Add 'Z' to indicate UTC
    const now = new Date();

    // Calculate elapsed time in seconds
    const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);

    // Handle negative elapsed time (should not happen now)
    if (elapsed < 0) {
      return '0s';
    }

    if (elapsed < 60) {
      return `${elapsed}s`;
    } else {
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      if (minutes < 60) {
        return `${minutes}m ${seconds}s`;
      } else {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
      }
    }
  };

  const getEditingDuration = () => {
    if (!selectedSleepEntry) return '';
    return formatDuration(selectedSleepEntry.duration_minutes);
  };

  if (loading) {
    return (
      <div className="track-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="track-page">
      {/* Sleep Wheel Visualization */}
      <SleepWheel
        sleepEntries={sleepEntries}
        ongoingSleep={ongoingSleep}
        onSleepClick={handleSleepEntryClick}
        use24h={user.time_format_24h}
        nextPrediction={nextPrediction}
        onPredictionClick={() => navigate('/predictions')}
        isLoadingPrediction={loadingPrediction}
      />

      {/* Floating Add Button */}
      <button
        className="floating-add-btn"
        onClick={() => setShowManualEntry(true)}
        aria-label="Add sleep manually"
      >
        +
      </button>

      {/* Sleep history list - temporarily hidden, will be moved to History page */}
      <div className="sleep-history" style={{ display: 'none' }}>
        <div className="sleep-history-header">
          <h2>{activeBaby?.name || 'Loading...'}</h2>
          <button 
            className="manual-entry-btn"
            onClick={() => setShowManualEntry(true)}
          >
            + Add Sleep
          </button>
        </div>
        <div className="sleep-content" ref={sleepListRef}>
          {sleepEntries.length === 0 ? (
            <p className="no-entries">No sleep entries yet. Start tracking!</p>
          ) : (
            <div className="sleep-list">
              {(() => {
                const groupedEntries = groupEntriesByDate(sleepEntries);
                const sortedDates = Object.keys(groupedEntries).sort((a, b) => 
                  new Date(a).getTime() - new Date(b).getTime()
                );

                return sortedDates.map(dateKey => (
                  <div key={dateKey} className="date-group">
                    <div className="date-header">
                      <h3>{getDateHeader(dateKey)}</h3>
                    </div>
                    {groupedEntries[dateKey]
                      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
                      .map((entry) => (
                        <div 
                          key={entry.id} 
                          className={`sleep-entry ${entry.is_ongoing ? 'ongoing' : ''} clickable`}
                          onClick={() => handleSleepEntryClick(entry)}
                        >
                          <div className="sleep-time">
                            <div className="start-time">
                              <strong>Start:</strong> {formatDateTimeForUser(entry.start_time)}
                            </div>
                            {entry.end_time && (
                              <div className="end-time">
                                <strong>End:</strong> {formatDateTimeForUser(entry.end_time)}
                              </div>
                            )}
                          </div>
                          <div className="sleep-duration">
                            {formatDuration(entry.duration_minutes)}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>

      <div className="tracking-controls">
        {isEditingEntry ? (
          /* Edit mode - show inline edit UI with buttons only */
          <div className="edit-controls">
            <div className="edit-header">
              <h3>Edit Sleep - {getEditingDuration()}</h3>
              {isEditingAfterStop && (
                <p className="edit-helper-text">Sleep saved, edit if you need</p>
              )}
            </div>

            <div className="edit-form">
              {/* Start time adjustment */}
              <div className="edit-time-section">
                <div className="edit-time-label">
                  Start: {selectedSleepEntry && formatTimeOnly(selectedSleepEntry.start_time)}
                </div>
                <div className="edit-arrow-controls">
                  <button
                    className="edit-arrow-btn"
                    onClick={() => handleAdjustEditStart(-1)}
                    title="Move start time back 1 minute"
                  >
                    ◄
                  </button>
                  <button
                    className="edit-arrow-btn"
                    onClick={() => handleAdjustEditStart(1)}
                    title="Move start time forward 1 minute"
                  >
                    ►
                  </button>
                </div>
              </div>

              {/* End time adjustment */}
              <div className="edit-time-section">
                <div className="edit-time-label">
                  End: {selectedSleepEntry && selectedSleepEntry.end_time && formatTimeOnly(selectedSleepEntry.end_time)}
                </div>
                <div className="edit-arrow-controls">
                  <button
                    className="edit-arrow-btn"
                    onClick={() => handleAdjustEditEnd(-1)}
                    title="Move end time back 1 minute"
                  >
                    ◄
                  </button>
                  <button
                    className="edit-arrow-btn"
                    onClick={() => handleAdjustEditEnd(1)}
                    title="Move end time forward 1 minute"
                  >
                    ►
                  </button>
                </div>
              </div>
            </div>

            <div className="edit-actions">
              <button
                className="delete-btn-inline"
                onClick={handleDeleteFromEdit}
              >
                Delete
              </button>
              <button
                className="done-btn-inline"
                onClick={handleDoneEdit}
              >
                Done
              </button>
            </div>
          </div>
        ) : ongoingSleep ? (
          /* Ongoing mode - show start time adjustment UI */
          <div className="ongoing-controls">
            <button
              className="adjust-button adjust-back"
              onClick={() => adjustStartTime(-1)}
              title="Move start time back 1 minute"
            >
              <span className="adjust-icon">◄</span>
              <span className="adjust-label">1m</span>
            </button>

            <button
              className="track-button stop"
              onClick={stopSleep}
            >
              <div className="button-content">
                <div className="button-text">Stop Sleep</div>
                <div className="button-timer">{getOngoingDuration()}</div>
              </div>
            </button>

            <button
              className="adjust-button adjust-forward"
              onClick={() => adjustStartTime(1)}
              title="Move start time forward 1 minute"
            >
              <span className="adjust-label">1m</span>
              <span className="adjust-icon">►</span>
            </button>
          </div>
        ) : (
          /* Idle mode - show start button */
          <button
            className="track-button start"
            onClick={startSleep}
            disabled={!activeBaby}
          >
            <div className="button-content">
              <div className="button-text">Start Sleep</div>
            </div>
          </button>
        )}

        {ongoingSleep && (
          <div className={`ongoing-info ${startTimeChanged ? 'time-changed' : ''}`}>
            <p>Started: {formatDateTimeRelative(ongoingSleep.start_time, user.time_format_24h)}</p>
          </div>
        )}
      </div>

      <ManualSleepEntry
        isOpen={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        onSubmit={handleManualSleepSubmit}
        babyId={activeBaby?.id || ''}
        use24h={user.time_format_24h}
      />

      <EditSleepEntry
        isOpen={showEditEntry}
        onClose={() => {
          setShowEditEntry(false);
          setSelectedSleepEntry(null);
        }}
        onUpdate={handleUpdateSleepEntry}
        onDelete={handleDeleteSleepEntry}
        sleepEntry={selectedSleepEntry}
        use24h={user.time_format_24h}
      />
    </div>
  );
};

export default Track;